import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { observer, inject } from 'mobx-react';
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import StartGuide from "../components/layout/StartGuide";
import LoginNoticeModal from "../components/common/LoginNoticeModal";
import {ToastContainer} from "react-toastify";

import './Default.scss'

// 상담톡 iframe origin — embed.md 의 CHAT_ORIGIN 과 동일 (postMessage 검증용)
const CHAT_ORIGIN = 'https://onnomad.co.kr';

// 모듈 레벨 변수 — 페이지 이동해도 상태 유지
let _chatOpen = false;
const CHAT_SIDE_KEY = 'chatWidgetSide'; // 'right' | 'left'
const _initialChatSide = (() => {
  try { return localStorage.getItem(CHAT_SIDE_KEY) === 'left' ? 'left' : 'right'; }
  catch (e) { return 'right'; }
})();

const DefaultLayout = inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))(
  observer(({ children, route, isLogin, me }) => {
    const store = (me && me.store) || {};
    const chatUrl = `https://onnomad.co.kr/embed/chat?service=온리플&id=${encodeURIComponent(store.platformId || '')}&shopName=${encodeURIComponent(store.name || '')}`;
    const [isChatOpen, setIsChatOpen] = useState(_chatOpen);
    const [isChatClosing, setIsChatClosing] = useState(false);
    const isFreshOpen = useRef(false);
    // 스크롤 시 플로팅 버튼 축소 상태
    const [isShrunk, setIsShrunk] = useState(false);
    // 채팅 위젯 위치 (left | right)
    const [chatSide, setChatSide] = useState(_initialChatSide);
    // iframe 으로부터 수신한 메시지 상태
    const [unreadCount, setUnreadCount] = useState(0);
    // 가장 최근 수신 메시지 — 채팅이 닫혀 있을 때 아이콘 위 말풍선으로 노출
    const [latestPreview, setLatestPreview] = useState('');
    const iframeFocusedRef = useRef(false);
    const iframeVisibleRef = useRef(false);
    const isChatOpenRef = useRef(_chatOpen);
    useEffect(() => { isChatOpenRef.current = isChatOpen; }, [isChatOpen]);

    const toggleChatSide = (e) => {
      e.stopPropagation();
      e.preventDefault();
      const next = chatSide === 'right' ? 'left' : 'right';
      setChatSide(next);
      try { localStorage.setItem(CHAT_SIDE_KEY, next); } catch (err) {}
    };

    // iframe 임베드 모드 — 사이드바/헤더/플로팅버튼 숨김
    const isEmbed = (() => {
      try {
        if (typeof window === 'undefined') return false;
        const params = new URLSearchParams(window.location.search);
        if (params.get('embed') === '1') return true;
        // iframe 안에서 로드된 경우 자동 감지 (옵션)
        return window.self !== window.top;
      } catch (e) {
        return false;
      }
    })();

    if (isEmbed) {
      return (
        <div className="wrap embed-mode">
          <ToastContainer />
          <div className="contents contents--embed" route={route} style={{marginLeft: 0, width: '100%'}}>
            {children}
          </div>
        </div>
      );
    }

    // iframe → 부모 메시지 수신 (embed.md 의 onnomad-chat:* 이벤트 처리)
    useEffect(() => {
      const handleMessage = (event) => {
        if (event.origin !== CHAT_ORIGIN) return;
        const data = event.data || {};
        switch (data.type) {
          case 'onnomad-chat:visibility':
            iframeFocusedRef.current = !!data.hasFocus;
            iframeVisibleRef.current = !!data.isVisible;
            break;
          case 'onnomad-chat:new-message': {
            const preview = data.preview || '새 메시지가 도착했습니다.';
            // 관리자 메시지 누적 카운트 (iframe 이 unreadCount 를 함께 보내면 우선 사용)
            if (typeof data.unreadCount === 'number') setUnreadCount(data.unreadCount);
            else setUnreadCount((c) => c + 1);

            // 채팅 닫힌 상태(아이콘만 보이는 상태) 일 때만 말풍선으로 최근 메시지 노출.
            // 채팅창이 열려 있으면 사용자가 이미 메시지를 보고 있다고 간주.
            if (!isChatOpenRef.current) {
              setLatestPreview(preview);
            }
            break;
          }
          default:
            break;
        }
      };
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }, []);

    useEffect(() => {
      const SHRINK_AT = 120; // 120px 이상 스크롤되면 축소
      const el = document.querySelector('.contents');
      const getTop = () => {
        if (el) return el.scrollTop;
        return window.scrollY || document.documentElement.scrollTop || 0;
      };
      const onScroll = () => {
        setIsShrunk(getTop() > SHRINK_AT);
      };
      if (el) el.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return () => {
        if (el) el.removeEventListener('scroll', onScroll);
        window.removeEventListener('scroll', onScroll);
      };
    }, []);

    const handleChatOpen = (e) => {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      setIsChatClosing(false);
      isFreshOpen.current = true;
      _chatOpen = true;
      setIsChatOpen(true);
      // 채팅창을 열어 메시지 확인 — 미읽음 카운트와 말풍선 모두 초기화
      setUnreadCount(0);
      setLatestPreview('');
    };

    const handleChatClose = () => {
      setIsChatClosing(true);
      setTimeout(() => {
        _chatOpen = false;
        isFreshOpen.current = false;
        setIsChatOpen(false);
        setIsChatClosing(false);
      }, 400);
    };

    const getChatClass = () => {
      if (isChatClosing) return 'closing';
      if (isFreshOpen.current) return 'opening';
      return 'persisted';
    };

    return (
      <div className="wrap">430
        <ToastContainer />
        <Sidebar route={route} />
        <div className="contents" route={route}>
          <Header route={route} onChatOpen={handleChatOpen} />
          {children}


        </div>
        <StartGuide />

        {!isChatOpen && (
          <div className={'chat-floating-btn' + (isShrunk ? ' is-shrunk' : '') + (chatSide === 'left' ? ' is-left' : '')}
               onClick={handleChatOpen}
               title={unreadCount > 0 ? `읽지 않은 메시지 ${unreadCount}개` : '상담 채팅'}>
            <i className="material-icons">support_agent</i>
            {unreadCount > 0 && (
              <span className="chat-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
            <button className="chat-side-toggle"
                    onClick={toggleChatSide}
                    title={chatSide === 'right' ? '왼쪽으로 이동' : '오른쪽으로 이동'}>
              <i className="material-icons">{chatSide === 'right' ? 'chevron_left' : 'chevron_right'}</i>
            </button>
          </div>
        )}

        {/* 새 메시지 말풍선 — 아이콘 상단에 표시. 클릭 시 채팅 열림. */}
        {!isChatOpen && latestPreview && (
          <div className={'chat-bubble' + (isShrunk ? ' is-shrunk' : '') + (chatSide === 'left' ? ' is-left' : '')}
               onClick={handleChatOpen}
               role="button"
               title="클릭하여 채팅 확인">
            <button className="chat-bubble__close"
                    onClick={(e) => { e.stopPropagation(); setLatestPreview(''); }}
                    title="닫기">
              <i className="material-icons">close</i>
            </button>
            <div className="chat-bubble__text">{latestPreview}</div>
          </div>
        )}

        {/* 채팅 창 — store 로드 후 항상 마운트.
            isChatOpen 이 false 여도 iframe 은 살아있어 백그라운드에서 onnomad-chat:new-message 를 수신함.
            가시성은 .is-hidden 클래스로 토글 (visibility/opacity/pointer-events). */}
        {store && store.platformId && (
          <div className={
            `chat-fixed-wrap ${getChatClass()}`
            + (chatSide === 'left' ? ' is-left' : '')
            + (!isChatOpen && !isChatClosing ? ' is-hidden' : '')
          }>
            <div className="chat-fixed-actions">
              <button className="chat-fixed-side-toggle"
                      onClick={toggleChatSide}
                      title={chatSide === 'right' ? '왼쪽으로 이동' : '오른쪽으로 이동'}>
                <i className="material-icons">{chatSide === 'right' ? 'chevron_left' : 'chevron_right'}</i>
              </button>
              <button className="chat-fixed-close" onClick={handleChatClose} title="최소화">
                <i className="material-icons">remove</i>
              </button>
            </div>
            <iframe
              src={chatUrl}
              title="온리플 채팅"
              className="chat-iframe"
            />
          </div>
        )}

        {/* 로그인 안내 팝업 — 페이지 그룹별(displayPages) 노출 */}
        <LoginNoticeModal />
      </div>
    );
  })
);

DefaultLayout.propTypes = {
  noNavbar: PropTypes.bool,
  noFooter: PropTypes.bool
};

DefaultLayout.defaultProps = {
  noNavbar: false,
  noFooter: true
};

export default DefaultLayout;
